import * as Portal from '@radix-ui/react-portal'
import { useCallback, useEffect } from 'react'
import { match } from 'ts-pattern'
import { type Toast, useToastService } from '../services/toast-service'
import { ErrorBoundary } from './error-boundaries/error-boundary'
import { Toast as ToastComponent } from './toast'
import * as styles from './toasts-overlay.css'

export const ToastsOverlay: React.FC = () => {
  const { toasts } = useToastService()

  return (
    <Portal.Root asChild>
      <div className={styles.overlay}>
        {/* When we start limiting the number of visible toasts (right now we show them all at once), we should
         * probably add a `priority` field to `Toast` and use it to control the order in which toasts are displayed. */}
        {[...toasts.values()].map(toast => (
          <ToastWithTimeout key={toast.id} toast={toast} />
        ))}
      </div>
    </Portal.Root>
  )
}

const ToastWithTimeout: React.FC<{
  toast: Toast
}> = ({ toast }) => {
  const timeoutMs = match(toast.transience ?? 'default')
    .with('default', () => 3000)
    .with('sticky', () => undefined)
    .exhaustive()

  const { removeToast: _removeToast } = useToastService()
  const removeToast = useCallback(() => _removeToast(toast.id), [_removeToast, toast.id])

  // It's tempting to manage the timeout from within the ToastService itself. However, we may eventually want to limit
  // the number of toasts visible at once (i.e., display the first 3 toasts and wait until one of them is dismissed).
  // In that case, we'll want the timeout to start when the toast is mounted, not when it is added to the queue.
  useEffect(() => {
    if (timeoutMs === undefined) {
      return undefined
    }

    const timeout = setTimeout(removeToast, timeoutMs)

    return () => clearTimeout(timeout)
  }, [removeToast, timeoutMs])

  const isSticky = toast.transience === 'sticky'

  return (
    <ErrorBoundary fallbackTitle="Unable to display notification">
      <ToastComponent variant={toast.variant} onClose={isSticky ? removeToast : undefined}>
        {toast.message}
      </ToastComponent>
    </ErrorBoundary>
  )
}
